import React, { useState } from "react";
import { set, useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import httpServices from "../../../services/httpServices";
import { useUser } from "../../../context/UserContext";
import { toast } from "react-toastify";
import LoadingOverlay from "../../../component/common/overlay";

const venueDimensionValidationSchema = Yup.object().shape({
  areaSqft: Yup.number()
    .typeError("Invalid input")
    .required("Area (sqft) is required")
    .positive("Area (sqft) must be a positive number"),
  lengthFt: Yup.number()
    .typeError("Invalid input")
    .required("Length (ft) is required")
    .positive("Length (ft) must be a positive number"),
  breadthFt: Yup.number()
    .typeError("Invalid input")
    .required("Breadth (ft) is required")
    .positive("Breadth (ft) must be a positive number"),
  heightFt: Yup.number()
    .typeError("Invalid input")
    .required("Height (ft) is required")
    .positive("Height (ft) must be a positive number"),
  areaSqm: Yup.number()
    .typeError("Invalid input")
    .required("Area (sqm) is required")
    .positive("Area (sqm) must be a positive number"),
  lengthM: Yup.number()
    .typeError("Invalid input")
    .required("Length (m) is required")
    .positive("Length (m) must be a positive number"),
  breadthM: Yup.number()
    .typeError("Invalid input")
    .required("Breadth (m) is required")
    .positive("Breadth (m) must be a positive number"),
  heightM: Yup.number()
    .typeError("Invalid input")
    .required("Height (m) is required")
    .positive("Height (m) must be a positive number"),
  minPersonCapacity: Yup.number()
    .typeError("Invalid input")
    .required("Min person capacity is required")
    .positive("Min person capacity must be a positive number")
    .integer("Min person capacity must be an integer"),
  maxPersonCapacity: Yup.number()
    .typeError("Invalid input")
    .required("Max person capacity is required")
    .positive("Max person capacity must be a positive number")
    .integer("Max person capacity must be an integer")
    .moreThan(
      Yup.ref("minPersonCapacity"),
      "Max person capacity must be greater than Min person capacity"
    ),
});

function VenueDimension({ venueId, setActiveStep, venueData }) {
  const { user } = useUser();
  const [loading, setLoading] = useState(false);
   const defaultValues = {
     areaSqft:
       venueData?.venueDimension?.find((d) => d.name === "Area(Sqft)")?.value ||
       "",
     lengthFt:
       venueData?.venueDimension?.find((d) => d.name === "Length(ft)")?.value ||
       "",
     breadthFt:
       venueData?.venueDimension?.find((d) => d.name === "Breath(ft)")?.value ||
       "",
     heightFt:
       venueData?.venueDimension?.find((d) => d.name === "height(ft)")?.value ||
       "",
     areaSqm:
       venueData?.venueDimension?.find((d) => d.name === "Area(Sqm)")?.value ||
       "",
     lengthM:
       venueData?.venueDimension?.find((d) => d.name === "Length(m)")?.value ||
       "",
     breadthM:
       venueData?.venueDimension?.find((d) => d.name === "Breath(m)")?.value ||
       "",
     heightM:
       venueData?.venueDimension?.find((d) => d.name === "height(m)")?.value ||
       "",
     minPersonCapacity: venueData?.venueMinPersonCapacity || "",
     maxPersonCapacity: venueData?.venueMaxPersonCapacity || "",
   };

   const {
     register,
     handleSubmit,
     formState: { errors },
     clearErrors,
   } = useForm({
     resolver: yupResolver(venueDimensionValidationSchema),
     defaultValues: defaultValues,
   });

   const onSubmit = async (data) => {
     // Transforming the form data into the required structure
     setLoading(true);
     const transformedData = {
       venueDimension: [
         { name: "Area(Sqft)", value: data.areaSqft, unit: "sqft" },
         { name: "Length(ft)", value: data.lengthFt, unit: "ft" },
         { name: "Breath(ft)", value: data.breadthFt, unit: "ft" },
         { name: "height(ft)", value: data.heightFt, unit: "ft" },
         { name: "Area(Sqm)", value: data.areaSqm, unit: "sqm" },
         { name: "Length(m)", value: data.lengthM, unit: "m" },
         { name: "Breath(m)", value: data.breadthM, unit: "m" },
         { name: "height(m)", value: data.heightM, unit: "m" },
       ],
       venueMinPersonCapacity: data.minPersonCapacity,
       venueMaxPersonCapacity: data.maxPersonCapacity,
     };

     try {
       const { response, error } = await httpServices.put(
         `/venue/update/${venueId}`,
         {
           ...data,
           stepsDone: 4,
           userId: user?.userId,
           venueDimension: transformedData.venueDimension,
           venueMinPersonCapacity: transformedData.venueMinPersonCapacity,
           venueMaxPersonCapacity: transformedData.venueMaxPersonCapacity,
         }
       );

       if (error) {
         throw error;
       }

       if (response.status === 200) {
         toast.success("Venue dimension saved successfully");
         setActiveStep(4);
       }
     } catch (error) {
       toast.error(error?.response?.data?.message || "Something went wrong");
     } finally {
       setLoading(false);
     }
   };

   const onError = (error) => {
     if (venueData === null || venueData?.stepsDone < 3) {
       toast.warn("Please complete the previous step");
       clearErrors();
     }
   };

   return (
     <>
       <form onSubmit={handleSubmit(onSubmit, onError)}>
         <div className="mt-3 p-3 bg-white relative">
           {loading && <LoadingOverlay />}
           <h3 className="text-blue fw-semibold text-xl mb-4">
             Venue Dimension
           </h3>
           <div className="row row-cols-lg-4 row-cols-md-3 row-cols-2">
             {/* Area (sqft) Field */}
             <div className="col mb-3">
               <label className="font-medium text-base mb-2">Area (sqft)</label>
               <input
                 type="text"
                 placeholder="sqft"
                 className={`custom-input bg-[#F5F6FA] ${
                   errors.areaSqft ? "border-red-500" : ""
                 }`}
                 {...register("areaSqft")}
               />
               {errors.areaSqft && (
                 <p className="text-red-500 text-sm">
                   {errors.areaSqft.message}
                 </p>
               )}
             </div>
             {/* Length (ft) Field */}
             <div className="col mb-3">
               <label className="font-medium text-base mb-2">Length (ft)</label>
               <input
                 type="text"
                 placeholder="ft"
                 className={`custom-input bg-[#F5F6FA] ${
                   errors.lengthFt ? "border-red-500" : ""
                 }`}
                 {...register("lengthFt")}
               />
               {errors.lengthFt && (
                 <p className="text-red-500 text-sm">
                   {errors.lengthFt.message}
                 </p>
               )}
             </div>
             {/* Breadth (ft) Field */}
             <div className="col mb-3">
               <label className="font-medium text-base mb-2">
                 Breadth (ft)
               </label>
               <input
                 type="text"
                 placeholder="ft"
                 className={`custom-input bg-[#F5F6FA] ${
                   errors.breadthFt ? "border-red-500" : ""
                 }`}
                 {...register("breadthFt")}
               />
               {errors.breadthFt && (
                 <p className="text-red-500 text-sm">
                   {errors.breadthFt.message}
                 </p>
               )}
             </div>
             {/* Height (ft) Field */}
             <div className="col mb-3">
               <label className="font-medium text-base mb-2">Height (ft)</label>
               <input
                 type="text"
                 placeholder="ft"
                 className={`custom-input bg-[#F5F6FA] ${
                   errors.heightFt ? "border-red-500" : ""
                 }`}
                 {...register("heightFt")}
               />
               {errors.heightFt && (
                 <p className="text-red-500 text-sm">
                   {errors.heightFt.message}
                 </p>
               )}
             </div>
             {/* Area (sqm) Field */}
             <div className="col mb-3">
               <label className="font-medium text-base mb-2">Area (sqm)</label>
               <input
                 type="text"
                 placeholder="sqm"
                 className={`custom-input bg-[#F5F6FA] ${
                   errors.areaSqm ? "border-red-500" : ""
                 }`}
                 {...register("areaSqm")}
               />
               {errors.areaSqm && (
                 <p className="text-red-500 text-sm">
                   {errors.areaSqm.message}
                 </p>
               )}
             </div>
             {/* Length (m) Field */}
             <div className="col mb-3">
               <label className="font-medium text-base mb-2">Length (m)</label>
               <input
                 type="text"
                 placeholder="m"
                 className={`custom-input bg-[#F5F6FA] ${
                   errors.lengthM ? "border-red-500" : ""
                 }`}
                 {...register("lengthM")}
               />
               {errors.lengthM && (
                 <p className="text-red-500 text-sm">
                   {errors.lengthM.message}
                 </p>
               )}
             </div>
             {/* Breadth (m) Field */}
             <div className="col mb-3">
               <label className="font-medium text-base mb-2">Breadth (m)</label>
               <input
                 type="text"
                 placeholder="m"
                 className={`custom-input bg-[#F5F6FA] ${
                   errors.breadthM ? "border-red-500" : ""
                 }`}
                 {...register("breadthM")}
               />
               {errors.breadthM && (
                 <p className="text-red-500 text-sm">
                   {errors.breadthM.message}
                 </p>
               )}
             </div>
             {/* Height (m) Field */}
             <div className="col mb-3">
               <label className="font-medium text-base mb-2">Height (m)</label>
               <input
                 type="text"
                 placeholder="m"
                 className={`custom-input bg-[#F5F6FA] ${
                   errors.heightM ? "border-red-500" : ""
                 }`}
                 {...register("heightM")}
               />
               {errors.heightM && (
                 <p className="text-red-500 text-sm">
                   {errors.heightM.message}
                 </p>
               )}
             </div>
             {/* Min Person Capacity Field */}
             <div className="col mb-3">
               <label className="font-medium text-base mb-2">
                 Min Person Capacity
               </label>
               <input
                 type="text"
                 placeholder="Min capacity"
                 className={`custom-input bg-[#F5F6FA] ${
                   errors.minPersonCapacity ? "border-red-500" : ""
                 }`}
                 {...register("minPersonCapacity")}
               />
               {errors.minPersonCapacity && (
                 <p className="text-red-500 text-sm">
                   {errors.minPersonCapacity.message}
                 </p>
               )}
             </div>
             {/* Max Person Capacity Field */}
             <div className="col mb-3">
               <label className="font-medium text-base mb-2">
                 Max Person Capacity
               </label>
               <input
                 type="text"
                 placeholder="Max capacity"
                 className={`custom-input bg-[#F5F6FA] ${
                   errors.maxPersonCapacity ? "border-red-500" : ""
                 }`}
                 {...register("maxPersonCapacity")}
               />
               {errors.maxPersonCapacity && (
                 <p className="text-red-500 text-sm">
                   {errors.maxPersonCapacity.message}
                 </p>
               )}
             </div>
           </div>
         </div>
         <div className="h-48 flex justify-center items-end">
           <button type="submit" className="gradian-btn text-white px-3 py-2">
             Save & Proceed
           </button>
         </div>
       </form>
     </>
   );
}

export default VenueDimension;
